import cn from "classnames";
import {
  FC,
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { isDesktop, isMobile } from "react-device-detect";
import { useDispatch, useSelector } from "react-redux";
import { setQueryParams } from "src/store/actionCreators/queryParams";
import { requestRaces } from "src/store/actionCreators/races";

import { ReactSVG } from "react-svg";
import { useGameBoxGameUrl } from "../../hoc/useGameBoxGameUrl";
import { useQuery } from "../../hoc/useQuery";
import { useSetGameBoxType } from "../../hoc/useSetGameBoxType";
import {
  requestCasinoNextInstances,
  requestGameBoxMission,
} from "../../store/actionCreators/gameBoxMission";
import { resetGameUrl } from "../../store/actionCreators/gameUrl";
import { requestProgressiveMissions } from "../../store/actionCreators/progressiveMissions";
import { changeGameBoxView } from "../../store/actionCreators/ui";
import {
  getDefaultGameBoxCategory,
  getGameBoxFunMission,
  getGameBoxMissionActualData,
  getGameBoxMissionCount,
  getGameBoxView,
  getGameInfo,
  getGameUrl,
  getIsClubApp,
  getModuleView,
  getMyBonusesData,
  getSelectedGameBoxData,
  getSelectedGameBoxTag,
  getToken,
} from "../../store/selectors";
import { isValidURL } from "../../utils/utils";
import Categories from "../Categories";
import GameBoxInformation from "../GameBoxInformation";
import GameHeader from "../GameHeader";
import MissionTitleAndDescriptionWidget from "../MissionTitleAndDescriptionWidget";
import MyBonuses from "../MyBonuses";
import Race from "../Race";

import { GameLoader } from "../GameLoader";
import Version from "../Version";
import "./styles.scss";

const GameModeViewPage: FC = () => {
  const queries = useQuery();
  const dispatch = useDispatch();
  const token = useSelector(getToken);
  const moduleView = useSelector(getModuleView);
  const isClubApp = useSelector(getIsClubApp) === "true";
  const selectedGameBoxData = useSelector(getSelectedGameBoxData);
  const iframeUrl = useSelector(getGameUrl);
  const gameBoxGameUrl = useGameBoxGameUrl();
  const gameBoxMissionCount = useSelector(getGameBoxMissionCount);
  const gameBoxMission = useSelector(getGameBoxMissionActualData);
  const gameBoxType = useSelector(getSelectedGameBoxTag);
  const gameBoxView = useSelector(getGameBoxView);
  const isFunMission = useSelector(getGameBoxFunMission);
  const setGameBoxTag = useSetGameBoxType();
  const gameInfo = useSelector(getGameInfo);
  const missionData = useSelector(getMyBonusesData);
  const defaultGameBoxType = useSelector(getDefaultGameBoxCategory);
  const [gameBoxWidgetVisibility, setGameBoxWidgetVisibility] =
    useState<boolean>(false);
  const missionsTitle = ["race"];
  const isGame = gameBoxView === "game";
  const [isLandscape, setIsLandscape] = useState<boolean>(
    window.matchMedia("(orientation: landscape)").matches
  );
  const [isOpen, setIsOpen] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(true);
  const gameBoxWidgetVisibilityHandler = (widgetVisibility?: boolean) => {
    setGameBoxWidgetVisibility(widgetVisibility ?? !gameBoxWidgetVisibility);
  };
  const raceId = localStorage.getItem("raceId");
  const [storedRaceValue, setStoredRaceValue] = useState<string | null>(raceId);
  const prevValueRef = useRef(storedRaceValue);
  const closeGameAudio = useMemo(
    () => new Audio("static/sounds/gb_close.mp3"),
    []
  );
  const validGameUrl =
    (iframeUrl && isValidURL(iframeUrl)) ||
    (gameBoxGameUrl && isValidURL(gameBoxGameUrl));

  const renderVersion = useMemo(() => {
    if (isMobile) {
      if (gameBoxWidgetVisibility) {
        return <Version className="gameMode-version" />;
      } else {
        return null;
      }
    } else {
      return <Version className="gameMode-version" />;
    }
  }, [gameBoxWidgetVisibility]);

  useLayoutEffect(() => {
    dispatch(setQueryParams(queries));
  }, [queries]);

  useEffect(() => {
    if (moduleView) {
      dispatch(changeGameBoxView(moduleView));
    }
  }, [moduleView]);

  useEffect(() => {
    const mediaQuery = window.matchMedia("(orientation: landscape)");
    const handleOrientationChange = () => setIsLandscape(mediaQuery.matches);
    mediaQuery.addEventListener("change", handleOrientationChange);

    return () => {
      mediaQuery.removeEventListener("change", handleOrientationChange);
    };
  }, []);

  useEffect(() => {
    if (isMobile && !isLandscape) {
      setIsOpen(false);
    }
  }, [isLandscape]);

  useEffect(() => {
    if (gameBoxView === "game") {
      window.parent.postMessage({ name: "offInactivity" }, "*");
    } else {
      window.parent.postMessage({ name: "clickAction" }, "*");
    }
  }, [gameBoxView]);

  const handleClick = () => {
    window.parent.postMessage({ name: "clickAction" }, "*");
  };

  useEffect(() => {
    if (gameBoxMission) {
      const tournamentMission = gameBoxMission.filter(
        (g: { categories: string | string[] }) =>
          g?.categories.includes("tourTicket")
      );
      tournamentMission.forEach(
        (t: {
          prize: {
            tourInstanceId: number | string;
            templateId: number | string;
          };
        }) => {
          dispatch(
            requestCasinoNextInstances({
              tourInstanceId: t?.prize.tourInstanceId,
              templateId: t?.prize.templateId,
            })
          );
        }
      );
    }
  }, [gameBoxMission]);

  useEffect(() => {
    if (token) {
      dispatch(requestProgressiveMissions());
      dispatch(requestGameBoxMission());
      dispatch(requestRaces());

      // Set up polling interval
      const intervalId = setInterval(() => {
        dispatch(requestProgressiveMissions());
        dispatch(requestGameBoxMission());
        dispatch(requestRaces());
      }, 600000); // Poll every 10 minute

      return () => clearInterval(intervalId);
    }

    // Return a noop cleanup function if no token is available
    return () => {};
  }, [token]);

  useEffect(() => {
    const intervalId = setInterval(() => {
      const currentValue = localStorage.getItem("raceId");
      if (prevValueRef.current !== currentValue) {
        clearInterval(intervalId);
        setStoredRaceValue(currentValue);
      }
      prevValueRef.current = currentValue;
    }, 0);
    return () => clearInterval(intervalId);
  }, [raceId, gameBoxView, storedRaceValue]);

  const onClose = () => {
    setGameBoxWidgetVisibility(false);
    setLoading(true);
    setIsOpen(false);
    closeGameAudio.play().catch(console.error);
    localStorage.removeItem("missionCompleted");
    window.parent.postMessage({ type: "CLOSE_GAME_BOX" }, "*");
    dispatch(changeGameBoxView(""));
    dispatch(resetGameUrl());
  };

  useEffect(() => {
    if (token && gameBoxMissionCount) {
      window.parent.postMessage(
        {
          type: "CHANGE_GAME_BOX_NOTIFICATION_COUNT",
          count: gameBoxMissionCount,
        },
        "*"
      );
    }
  }, [gameBoxMissionCount, token]);

  useEffect(() => {
    if (!!gameInfo?.bonusMissionId) {
      setGameBoxTag("funMission");
    } else {
      setGameBoxTag(defaultGameBoxType);
    }
  }, [defaultGameBoxType, gameInfo, setGameBoxTag]);

  const handleFrameMessages = useCallback((e: any) => {
    const gameCloseMessagesArray = [
      "close",
      "closeGame",
      "notifyCloseContainer",
      "GAME_MODE:LOBBY",
      "closeFrame",
    ];
    if (gameCloseMessagesArray.includes(e.data.type)) {
      onClose();
    }
  }, []);

  useEffect(() => {
    window.addEventListener("message", handleFrameMessages);
    return () => window.removeEventListener("message", handleFrameMessages);
  }, [handleFrameMessages]);

  const toggleSidebar = () => {
    setIsOpen(!isOpen);
  };

  const handleIframeLoad = () => {
    setLoading(false);
  };

  if (gameBoxView === "game" && validGameUrl) {
    return (
      <div
        className={cn("game-mod-view", {
          mobile: isMobile,
          landscape: !gameBoxWidgetVisibility && isLandscape && isMobile,
          "landscape-without-missions":
            !selectedGameBoxData && !gameBoxType && isLandscape && isMobile,
        })}
      >
        {(isDesktop || (!selectedGameBoxData && !gameBoxType)) && (
          <GameHeader onClose={onClose} isClubApp={isClubApp} />
        )}
        {(isDesktop || isLandscape) &&
          !gameBoxWidgetVisibility &&
          selectedGameBoxData && (
            <div
              className={cn("toggle-sidebar", { "rotate-icon": isOpen })}
              onClick={toggleSidebar}
            >
              <ReactSVG src="/static/images/toggle_sidebar_arrow.svg" />
            </div>
          )}
        {loading && (
          <GameLoader
            className={cn({ "loader-center": !selectedGameBoxData })}
          />
        )}
        <iframe
          src={iframeUrl || gameBoxGameUrl}
          allowFullScreen={!isMobile}
          frameBorder={0}
          width="100%"
          height="100%"
          title="gameMode"
          onLoad={handleIframeLoad}
          className={cn({
            "game-widget-visibility": gameBoxWidgetVisibility,
            fullScreen: !selectedGameBoxData && !gameBoxType,
            "sidebar-close":
              isOpen && selectedGameBoxData && (isDesktop || isLandscape),
            "sidebar-open":
              !isOpen && selectedGameBoxData && (isDesktop || isLandscape),
          })}
        />
        {gameBoxType && !isOpen && (
          <div
            className={cn("game-mod-view__content", {
              "game-mod-view__header": isDesktop,
              "landscape-missions": isLandscape && isMobile,
            })}
          >
            <Categories
              gameBoxType={gameBoxType}
              closeGame={onClose}
              gameBoxWidgetVisibilityHandler={gameBoxWidgetVisibilityHandler}
              gameBoxWidgetVisibility={gameBoxWidgetVisibility}
              isGame={isGame}
              isLandscape={isLandscape}
              storedRaceValue={storedRaceValue}
            />
            {isDesktop && (
              <MissionTitleAndDescriptionWidget
                mission={missionData?.[gameBoxType]?.[0]}
                storedRaceValue={storedRaceValue}
              />
            )}
            {(gameBoxWidgetVisibility || isDesktop) && (
              <>
                <Race isLandscape={isLandscape} />
                {!missionsTitle.includes(gameBoxType) && (
                  <GameBoxInformation
                    data={selectedGameBoxData}
                    isFunMission={isFunMission}
                    isLandscape={isLandscape}
                    onClose={onClose}
                    gameBoxWidgetVisibilityHandler={
                      gameBoxWidgetVisibilityHandler
                    }
                  />
                )}
              </>
            )}
            {renderVersion}
          </div>
        )}
        {isOpen && (isDesktop || isLandscape) && (
          <div className="game-box-sidebar-content">
            <div className="game-box-info">
              <img src="/static/images/box.png" width={70} alt="box" />
              <p>
                Game<span>Box</span>
              </p>
            </div>
          </div>
        )}
      </div>
    );
  }

  if (gameBoxView === "widget" && !isClubApp) {
    return (
      <div onClick={handleClick}>
        <MyBonuses
          isLandscape={isLandscape}
          storedRaceValue={storedRaceValue}
        />
      </div>
    );
  }

  return null;
};

export default GameModeViewPage;
